import type { Cancellation } from "../types/async.ts";
import { eventYieldingFor } from "../util/sleep.ts";
import { keyOf } from "./graph.ts";
import type { NodePredicate, NodeSearch } from "./search.ts";
import { TRUE_SEARCH, paths, reachable } from "./search.ts";
import type { GraphSearchSettings } from "./settings.ts";
import type { ConnectedNode, DirectedGraph, GraphOf, Path } from "./types.ts";

/** Creates a node search that returns 0-hop paths for a "from" predicated */
const init = <D extends DirectedGraph<any>>(
  from: NodePredicate<GraphOf<D>>
) => ({ ...TRUE_SEARCH, predicate: from });

/** Returns all nodes that match zero or more search terms
 *
 * Releases thread priority during run.
 */
export const discover = async <D extends DirectedGraph<any>>(
  graph: D,
  from: NodePredicate<GraphOf<D>>,
  search: NodeSearch<GraphOf<D>>[]
): Promise<D> => {
  let subgraph = await reachable(graph, from, init(from));
  await eventYieldingFor(
    search,
    async (s) => {
      subgraph = await reachable(subgraph, from, s);
    },
    { maxOccupancyMs: 100 }
  );
  return subgraph;
};

export type DiscoverMatch<D extends DirectedGraph<any>> = {
  node: ConnectedNode<GraphOf<D>, keyof GraphOf<D>>;
  matches: {
    query: string;
    paths: Path<GraphOf<D>>[];
  }[];
};

/** Returns all paths that match zero or more search terms
 *
 * Results are grouped by individual term.
 */
export const discoverPaths = async <D extends DirectedGraph<any>>(args: {
  graph: D;
  from: NodePredicate<GraphOf<D>>;
  search: NodeSearch<GraphOf<D>>[];
  settings?: Partial<GraphSearchSettings>;
  cancellation?: Cancellation;
}): Promise<DiscoverMatch<D>[]> => {
  const maxOccupancyMs = 100;
  const { graph, from, search, settings, cancellation } = args;
  const { nodes } = await reachable(graph, from, init(from));
  let matches: Record<string, DiscoverMatch<D>> = Object.fromEntries(
    nodes.map((node) => [keyOf<GraphOf<D>>(node), { node, matches: [] }])
  );
  const options = { ...settings, cancellation, maxOccupancyMs };

  await eventYieldingFor(
    search,
    async (s) => {
      const nodes = Object.values(matches).map((m) => m.node);
      const output: Record<string, DiscoverMatch<D>> = {};
      const found = await paths({ nodes } as GraphOf<D>, from, s, options);
      for (const match of found) {
        const nodeKey = keyOf(match.node);
        const previous = matches[nodeKey];
        if (!previous) continue;
        output[nodeKey] = {
          node: match.node,
          matches: [...previous.matches, { query: s.term, paths: match.paths }],
        };
      }
      matches = output;
    },
    { cancellation, maxOccupancyMs }
  );
  return Object.values(matches);
};
